// Reading the game's reports: what kind a report is, who fought whom, the seal it earns, the inbox line under the
// subject, and the day groups. Pure functions over the shapes in src/api/types.ts (docs/api-phase3.md).
import type { ReportBody, ReportListItem, ReportResources, ReportType, ReportUnits } from "@/api/types";
import { BUILDING_KEYS, BUILDING_NAMES, RESOURCE_KEYS, TROOP_KEYS, TROOP_NAMES, TROOP_PLURAL, TROOP_SLUG, type BuildingKey, type ResourceKey, type TroopKey } from "./constants";
import { fmtNum, parseCoords, plural } from "./format";
import { parseGameTime } from "./time";

export type Seal = "victory" | "losses" | "defeat" | "trade" | "support" | "spy" | "incoming" | "guild";
export type InboxTab = "attack" | "support" | "market";

/** Report bodies hold only the keys above zero; the phone's converter reads "-" as 0. */
export const num = (v: number | string | null | undefined): number => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

export const unitCount = (m: ReportUnits | undefined, k: TroopKey | string): number => num(m?.[k]);
export const sumUnits = (m: ReportUnits | undefined): number => (m ? TROOP_KEYS.reduce((a, k) => a + unitCount(m, k), 0) : 0);
export const sumResources = (m: ReportResources | undefined): number => (m ? RESOURCE_KEYS.reduce((a, k) => a + num(m[k]), 0) : 0);
export const hasAny = (m: Record<string, unknown> | undefined): boolean => !!m && Object.values(m).some((v) => num(v as number | string) > 0);

export const reportType = (b: ReportBody | undefined): ReportType | "unknown" => {
  const t = b?.report_type;
  return t === "re_a" || t === "re_d" || t === "re_s" || t === "re_t" || t === "re_gi" || t === "re_gu" || t === "re_is" || t === "re_i" ? t : "unknown";
};

/** The inbox's three tabs (Classic Frame, Oct 10 2026): ATTACK holds every battle on either side plus all scouting
 *  (instant spies and revealed incomings); SUPPORT the support reports; MARKET the trades. Guild invitations and guild
 *  notices are friendly business, so they sit under Support (plan decision D1). */
export const inboxTabOf = (b: ReportBody | undefined): InboxTab => {
  switch (reportType(b)) {
    case "re_a":
    case "re_d":
    case "re_is":
    case "re_i":
      return "attack";
    case "re_t":
      return "market";
    default:
      return "support";
  }
};

export const INBOX_TABS: { value: InboxTab; label: string }[] = [
  { value: "attack", label: "Attack" }, { value: "support", label: "Support" }, { value: "market", label: "Market" },
];

/** ?t= (and the old ?f= filters: battles / spy → attack, trade → market, support → support); null when neither names a tab. */
export function inboxTabParam(t: string | null, f?: string | null): InboxTab | null {
  if (t === "attack" || t === "support" || t === "market") return t;
  if (f === "battles" || f === "spy") return "attack";
  if (f === "trade") return "market";
  if (f === "support") return "support";
  return null;
}

export const inTab = (b: ReportBody | undefined, t: InboxTab): boolean => inboxTabOf(b) === t;

/** A battle report "belongs" to the attacker when it is an attack report; the defender's copy is re_d. */
export const isAttackerSide = (b: ReportBody): boolean => reportType(b) !== "re_d";

/** Did the recipient win? (attack reports: attacker win; defend reports: attacker loss.) */
export const recipientWon = (b: ReportBody): boolean | null => {
  if (b.is_attacker_win === undefined || b.is_attacker_win === null) return null;
  const aw = num(b.is_attacker_win) === 1;
  return isAttackerSide(b) ? aw : !aw;
};

/** Losses on the recipient's own side. */
export const ownLosses = (b: ReportBody): number => (isAttackerSide(b) ? sumUnits(b.dead_attacking_units) : sumUnits(b.dead_defending_units));
export const ownSent = (b: ReportBody): number => (isAttackerSide(b) ? sumUnits(b.attacking_units) : sumUnits(b.defending_units));

/** The wax seal: green victory without losses, amber victory with losses, red defeat; trade / support / spy their own. */
export function sealOf(b: ReportBody | undefined): Seal {
  switch (reportType(b)) {
    case "re_a":
    case "re_d": {
      const won = recipientWon(b!);
      if (won === false) return "defeat";
      return ownLosses(b!) > 0 ? "losses" : "victory";
    }
    case "re_t":
      return "trade";
    case "re_s":
      return "support";
    case "re_is":
      return "spy";
    case "re_i":
      return "incoming";
    default:
      return "guild";
  }
}

export const SEAL_WORD: Record<Seal, string> = {
  victory: "Victory", losses: "Victory", defeat: "Defeat", trade: "Trade complete", support: "Support", spy: "Spy report", incoming: "Incoming", guild: "Guild",
};

/** When the report happened, in ms: the body's report_time first, else the row's created_at. */
export const reportTime = (item: Pick<ReportListItem, "b" | "ts">): number => {
  const t = parseGameTime(item.b?.report_time);
  if (Number.isFinite(t)) return t;
  return typeof item.ts === "number" ? (item.ts > 1e12 ? item.ts : item.ts * 1000) : NaN;
};

/** The two parties of any report, as the inbox and the headline name them. */
export interface Party {
  login: string;
  user_id: number;
  city?: string;
  coords?: string;
}
export function parties(b: ReportBody): { from: Party; to: Party } {
  const mk = (c?: { login: string; user_id: number }, v?: { city_name: string; coords: string }): Party => ({ login: c?.login ?? "", user_id: num(c?.user_id), city: v?.city_name, coords: v?.coords });
  const t = reportType(b);
  if (t === "re_a" || t === "re_d" || t === "re_is") return { from: mk(b.attacking_char, b.attacking_village), to: mk(b.defending_char, b.defending_village) };
  if (b.offered_char || b.accepted_char) return { from: mk(b.offered_char, b.offered_village), to: mk(b.accepted_char, b.accepted_village) };
  return { from: mk(b.origin_char, b.origin_village), to: mk(b.target_char, b.target_village) };
}

export const isBarbarian = (p: Party): boolean => p.user_id === 0 || /barbarian/i.test(p.login);

/** The subject with the rich-text size tag the phone uses for barbarians stripped. */
export const cleanSubject = (s: string | undefined): string => (s ?? "").replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();

/** The short line under a subject in the inbox: "Barbarian (508, 519) · no losses", "the wall · 100 wood for 100 clay"… */
export function inboxLine(b: ReportBody): string {
  const t = reportType(b);
  const { from, to } = parties(b);
  const who = (p: Party) => (isBarbarian(p) ? "Barbarian" : p.login || "Unknown");
  const where = (p: Party) => (p.coords ? ` (${p.coords.replace(",", ", ")})` : "");
  switch (t) {
    case "re_a":
    case "re_d": {
      const other = isAttackerSide(b) ? to : from;
      const lost = ownLosses(b);
      const bits = [`${who(other)}${where(other)}`];
      bits.push(lost > 0 ? `${fmtNum(lost)} lost` : "no losses");
      if (b.resulting_wall_level !== undefined && b.original_wall_level !== undefined && num(b.resulting_wall_level) !== num(b.original_wall_level)) bits.push(`wall ${num(b.original_wall_level)} → ${num(b.resulting_wall_level)}`);
      else if (t === "re_d" && b.original_wall_level !== undefined) bits.push(`wall held at ${num(b.original_wall_level)}`);
      if (b.end_loyalty !== undefined && b.end_loyalty !== null && b.start_loyalty !== undefined && b.start_loyalty !== null) bits.push(`loyalty ${num(b.start_loyalty)} → ${num(b.end_loyalty)}`);
      return bits.join(" · ");
    }
    case "re_t": {
      const inn = resourceWords(b.in_resources);
      const out = resourceWords(b.out_resources);
      const other = b.offered_char ? who(from) : who(from) === who(to) ? who(to) : `${who(from)}`;
      if (inn && out) return `${other} · ${out} for ${inn}`;
      if (inn) return `${other} · ${inn} received`;
      if (out) return `${other} · ${out} sent`;
      return other;
    }
    case "re_s": {
      const n = sumUnits(b.units);
      const dead = sumUnits(b.dead_units);
      const lead = `${who(from)}${where(from)}`;
      if (dead > 0) return `${lead} · ${fmtNum(dead)} of ${fmtNum(n)} lost`;
      return `${lead} · ${n ? unitWords(b.units) + " arrived" : "arrived"}`;
    }
    case "re_is": {
      const nb = b.spied_buildings ? Object.keys(b.spied_buildings).filter((k) => num(b.spied_buildings![k]) > 0).length : 0;
      const bits = [`${to.coords ? `(${to.coords.replace(",", ", ")})` : who(to)}`];
      if (nb) bits.push(`${nb} buildings`);
      if (hasAny(b.spied_resources)) bits.push("resources");
      if (hasAny(b.defending_units)) bits.push(`${fmtNum(sumUnits(b.defending_units))} troops home`);
      return bits.join(" · ");
    }
    case "re_i": {
      const n = sumUnits(b.incoming_units);
      const land = parseGameTime(b.end_time);
      return `${who(from)}${where(from)} · ${plural(n, "unit")}${Number.isFinite(land) ? " revealed" : ""}`;
    }
    default:
      return "";
  }
}

/** The number on the right of an inbox row: hauled / lost / wall / rate. */
export function inboxValue(b: ReportBody): { label: string; value: string; tone?: "gold" | "crim" | "green" } | null {
  const t = reportType(b);
  switch (t) {
    case "re_a":
    case "re_d": {
      const won = recipientWon(b);
      if (isAttackerSide(b) && won && b.haul) return { label: "Hauled", value: fmtNum(sumResources(b.haul)), tone: "gold" };
      const lost = ownLosses(b);
      if (won === false || lost > 0) return { label: "Lost", value: fmtNum(lost), tone: "crim" };
      if (!isAttackerSide(b) && won) return { label: "Held", value: b.original_wall_level !== undefined ? `Wall ${num(b.resulting_wall_level ?? b.original_wall_level)}` : "✓", tone: "green" };
      return null;
    }
    case "re_t": {
      const inn = sumResources(b.in_resources);
      const out = sumResources(b.out_resources);
      if (inn && out) return { label: "Rate", value: rate(out, inn) };
      if (inn) return { label: "Received", value: `+${fmtNum(inn)}`, tone: "green" };
      if (out) return { label: "Sent", value: fmtNum(out) };
      return null;
    }
    case "re_s":
      return { label: "", value: sumUnits(b.dead_units) > 0 ? "Attacked" : "Stationed", tone: sumUnits(b.dead_units) > 0 ? "crim" : undefined };
    case "re_is": {
      const wall = b.spied_buildings ? num(b.spied_buildings.b_wa) : 0;
      return { label: "Wall", value: String(wall) };
    }
    case "re_i":
      return { label: "", value: fmtNum(sumUnits(b.incoming_units)), tone: "crim" };
    default:
      return null;
  }
}

/** "1 : 1", "1 : 1.5", "2 : 1" from two amounts. */
export function rate(a: number, b: number): string {
  if (!a || !b) return "—";
  const r = b / a;
  const f = (x: number) => (Math.abs(x - Math.round(x)) < 0.005 ? String(Math.round(x)) : x.toFixed(x < 10 ? 2 : 1).replace(/\.?0+$/, ""));
  return r >= 1 ? `1 : ${f(r)}` : `${f(1 / r)} : 1`;
}

/** "1,200 Guardians" when one kind marched, else "1,350 units". */
export function unitWords(m: ReportUnits | undefined): string {
  const kinds = TROOP_KEYS.filter((k) => unitCount(m, k) > 0);
  if (kinds.length === 1) return plural(unitCount(m, kinds[0]), TROOP_NAMES[kinds[0]], TROOP_PLURAL[kinds[0]]);
  return plural(sumUnits(m), "unit");
}

/** "100 wood", "100 wood, 200 clay" */
export function resourceWords(m: ReportResources | undefined): string {
  if (!m) return "";
  const names: Record<ResourceKey, string> = { r_w: "wood", r_c: "clay", r_i: "iron" };
  return RESOURCE_KEYS.filter((k) => num(m[k]) > 0).map((k) => `${fmtNum(num(m[k]))} ${names[k]}`).join(", ");
}

/** Unit rows for the attacker / defender tables: every troop with anything in any column. */
export interface UnitRow {
  key: TroopKey;
  name: string;
  plural: string;
  sent: number;
  lost: number;
  saved: number;
  left: number;
}
export function unitRows(sent: ReportUnits | undefined, lost?: ReportUnits, saved?: ReportUnits): UnitRow[] {
  const rows: UnitRow[] = [];
  for (const k of TROOP_KEYS) {
    const s = unitCount(sent, k), l = unitCount(lost, k), sv = unitCount(saved, k);
    if (s || l || sv) rows.push({ key: k, name: TROOP_NAMES[k], plural: TROOP_PLURAL[k], sent: s, lost: l, saved: sv, left: Math.max(0, s - l) });
  }
  return rows;
}

/** The fifteen buildings for the spy intel grid, in the game's order, with "—" for unbuilt. */
export function buildingRows(m: Partial<Record<string, number | string>> | undefined): { key: BuildingKey; name: string; level: number }[] {
  return BUILDING_KEYS.map((k) => ({ key: k, name: BUILDING_NAMES[k], level: num(m?.[k]) }));
}

/** The haul as a share of capacity, with the boosted part. */
export function haulInfo(b: ReportBody): { total: number; capacity: number; boost: number; full: boolean } | null {
  if (!b.haul) return null;
  const total = sumResources(b.haul);
  const capacity = num(b.haul_capacity);
  const boost = num(b.haul_capacity_additional);
  return { total, capacity, boost, full: capacity > 0 && total >= capacity };
}

// ------------------------------------------------------------------ the inbox

export interface InboxItem {
  id: string;
  item: ReportListItem;
  time: number;
  seal: Seal;
  tab: InboxTab;
  subject: string;
  line: string;
  value: ReturnType<typeof inboxValue>;
  unread: boolean;
}

/** The list in the server's order (nf_order, newest first), decorated for the inbox. */
export function inboxItems(list: { report_info?: { newsfeed?: Record<string, ReportListItem>; nf_order?: (number | string)[] } } | undefined): InboxItem[] {
  const feed = list?.report_info?.newsfeed;
  if (!feed) return [];
  const order = list?.report_info?.nf_order?.length ? list.report_info.nf_order.map(String) : Object.keys(feed);
  const out: InboxItem[] = [];
  for (const id of order) {
    const item = feed[id];
    if (!item) continue;
    const b = item.b ?? ({ report_type: "" } as ReportBody);
    out.push({ id, item, time: reportTime(item), seal: sealOf(b), tab: inboxTabOf(b), subject: cleanSubject(item.s), line: inboxLine(b), value: inboxValue(b), unread: num(item.r) === 0 });
  }
  out.sort((a, c) => (c.time || 0) - (a.time || 0));
  return out;
}

/** Today / Yesterday / weekday-or-date groups, in order. */
export function dayGroups<T extends { time: number }>(items: T[], now: number): { label: string; items: T[] }[] {
  const startOfDay = (ms: number) => { const d = new Date(ms); d.setUTCHours(0, 0, 0, 0); return d.getTime(); };
  const today = startOfDay(now);
  const groups: { label: string; items: T[] }[] = [];
  const idx = new Map<string, number>();
  for (const it of items) {
    const t = Number.isFinite(it.time) ? it.time : 0;
    const day = startOfDay(t);
    const diff = Math.round((today - day) / 86400e3);
    const label = !Number.isFinite(it.time) ? "Earlier" : diff <= 0 ? "Today" : diff === 1 ? "Yesterday" : diff < 7 ? new Date(t).toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" }) : new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
    let i = idx.get(label);
    if (i === undefined) { i = groups.length; idx.set(label, i); groups.push({ label, items: [] }); }
    groups[i].items.push(it);
  }
  return groups;
}

/** Hero numbers for the inbox: unread, today's count, hauled and lost today. */
export function inboxStats(items: InboxItem[], now: number): { unread: number; today: number; hauled: number; lost: number } {
  const d = new Date(now); d.setUTCHours(0, 0, 0, 0);
  const start = d.getTime();
  let unread = 0, today = 0, hauled = 0, lost = 0;
  for (const it of items) {
    if (it.unread) unread++;
    if (it.time >= start) {
      today++;
      const b = it.item.b;
      const t = reportType(b);
      if (t === "re_a" && recipientWon(b)) hauled += sumResources(b.haul);
      if (t === "re_a" || t === "re_d") lost += ownLosses(b);
    }
  }
  return { unread, today, hauled, lost };
}

/** The coordinates the ATTACK AGAIN button should aim at: the other side's city. */
export function targetOf(b: ReportBody): [number, number] | null {
  const { from, to } = parties(b);
  const other = reportType(b) === "re_d" ? from : to;
  return parseCoords(other.coords);
}

/** The Valor Tools battle simulator (valor-reforged-tools.com/sim) reads its battle from the address hash:
 *  `a.knight=2016&a.scout=14&d.sentry=1500&wall=12&morale=100&luck=-8` (unit slugs, `:level` optional). */
export const SIM_URL = "https://valor-reforged-tools.com/sim/";
export function simulatorHash(b: ReportBody): string {
  const parts: string[] = [];
  const side = (k: "a" | "d", m: ReportUnits | undefined) => {
    for (const t of TROOP_KEYS) {
      const n = unitCount(m, t);
      if (n > 0 && t !== "t_n") parts.push(`${k}.${TROOP_SLUG[t]}=${n}`);
    }
  };
  side("a", b.attacking_units ?? b.incoming_units);
  side("d", b.defending_units);
  const wall = b.original_wall_level ?? b.spied_buildings?.b_wa;
  if (wall !== undefined && wall !== null) parts.push(`wall=${num(wall)}`);
  if (b.morale !== undefined && num(b.morale) !== 100) parts.push(`morale=${num(b.morale)}`);
  if (b.luck !== undefined && num(b.luck) !== 0) parts.push(`luck=${num(b.luck)}`);
  return parts.join("&");
}
export const simulatorUrl = (b: ReportBody) => `${SIM_URL}#${simulatorHash(b)}`;
